import { computeTargetSize } from '../lib/compressImage';

/** Reexport deliberado: `computeTargetSize` vive en `lib/compressImage` (Task 8) y aquí
 *  NO se reimplementa. Quien importe `domain/photoStamp` la obtiene desde este módulo. */
export { computeTargetSize };

export interface StampInfo { dateTimeText: string; latLngText: string; pdvName: string; }

export interface CanvasFactory { create(w: number, h: number): { canvas: unknown;
  ctx: CanvasRenderingContext2D; toBlob(quality: number): Promise<Blob>; } }

/** Decoder inyectable: createImageBitmap no existe en jsdom. */
export type PhotoDecoder = (b: Blob) => Promise<{ width: number; height: number; bitmap: unknown }>;

export const MAX_SIDE_PX = 1600;
export const MAX_BYTES = 350 * 1024;

/**
 * 🔴 `OffscreenCanvas` NO EXISTE EN EL SAFARI DE MUCHOS IPHONE EN USO.
 *
 * Safari de iOS lo agregó recién en **16.4**. Antes de eso, `new OffscreenCanvas(...)` tira
 * `ReferenceError`, el sellado falla, y la pantalla de marcado contesta «No se pudo procesar la
 * foto. Vuelve a tomarla.» — un mensaje que invita a repetir algo que **no va a funcionar nunca en
 * ese teléfono**.
 *
 * Pasó en campo el 14-set: una impulsadora en el Mercado La Perla de Chimbote se pasó la jornada
 * tomando la foto de nuevo, y no pudo marcar ingreso en todo el día. Y era el único camino roto:
 * las fotos del formulario de Softys andaban bien en el mismo celular, porque `compressImage` usa
 * un `<canvas>` normal. Dos formas de hacer lo mismo, una sola rota.
 *
 * Por eso: se usa `OffscreenCanvas` cuando está, y si no, el canvas de toda la vida.
 */
export const defaultFactory: CanvasFactory = {
  create(w: number, h: number) {
    if (typeof OffscreenCanvas !== 'undefined') {
      const off = new OffscreenCanvas(w, h);
      const offCtx = off.getContext('2d');
      if (offCtx) {
        return {
          canvas: off,
          ctx: offCtx as unknown as CanvasRenderingContext2D,
          toBlob: (quality: number) => off.convertToBlob({ type: 'image/jpeg', quality }),
        };
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No se pudo crear el contexto 2D del canvas');
    return {
      canvas,
      ctx,
      // `toBlob` entrega `null` cuando el navegador no pudo generar la imagen —pasa con canvas
      // grandes en celulares con poca memoria—. Sin este rechazo explícito, el `null` seguía
      // viaje y se subía «una foto» que no era una foto.
      toBlob: (quality: number) => new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('El navegador no pudo generar la foto sellada.'))),
          'image/jpeg',
          quality,
        );
      }),
    };
  },
};

const defaultDecode: PhotoDecoder = async (b: Blob) => {
  // `imageOrientation: 'from-image'` por el mismo motivo que en `compressImage`: sin él, una foto
  // vertical de celular puede quedar acostada. Va en un intento aparte porque los Safari viejos
  // —los mismos que no tienen OffscreenCanvas— rechazan el objeto de opciones.
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(b, { imageOrientation: 'from-image' });
  } catch {
    bitmap = await createImageBitmap(b);
  }
  return { width: bitmap.width, height: bitmap.height, bitmap };
};

/** Sella la foto: redimensiona (lado mayor ≤1600), banda negra semitransparente
 *  inferior con fecha/hora, lat/lng y PDV en blanco; JPEG 0.7 y reintento a 0.55
 *  si supera 350 KB. */
export async function stampPhoto(source: Blob, info: StampInfo,
  factory: CanvasFactory = defaultFactory, decode: PhotoDecoder = defaultDecode): Promise<Blob> {
  const { width, height, bitmap } = await decode(source);
  const { width: tw, height: th } = computeTargetSize(width, height, MAX_SIDE_PX);

  const target = factory.create(tw, th);
  const ctx = target.ctx;

  ctx.drawImage(bitmap as CanvasImageSource, 0, 0, tw, th);

  // Banda inferior semitransparente
  const fontSize = Math.max(14, Math.round(tw * 0.026)); // proporcional al ancho, legible
  const lineGap = Math.round(fontSize * 1.3);
  const pad = Math.round(fontSize * 0.6);
  const bandH = pad * 2 + lineGap * 3;
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.fillRect(0, th - bandH, tw, bandH);

  // Tres líneas de texto blanco
  ctx.fillStyle = '#FFFFFF';
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textBaseline = 'bottom';
  const lines = [info.dateTimeText, info.latLngText, info.pdvName];
  lines.forEach((line, i) => {
    const y = th - pad - (lines.length - 1 - i) * lineGap;
    ctx.fillText(line, pad, y);
  });

  let blob = await target.toBlob(0.7);
  if (blob.size > MAX_BYTES) blob = await target.toBlob(0.55);
  return blob;
}
